import {
  decodeFunctionResult,
  encodeAbiParameters,
  encodeFunctionData,
  encodeFunctionResult,
  hashTypedData,
  keccak256,
  recoverTypedDataAddress,
  sha256,
  toBytes,
  toHex,
  type Abi,
  type Address,
  type Hex,
} from "viem";
import { botnetRegistryV2Abi } from "../../../shared/src/abi-v2";
import { REGISTRY_V2_CHAIN_ID, REGISTRY_V2_PROTOCOL } from "../../../shared/src/registry-v2-public";
import type { DeploymentReference, SystemPrompt } from "./public-v2";
import type { V2AdmissionVoucher, WalletOperation, WalletReservationResult } from "./wallet-auth";
import { encodeV2Utf8Prompt } from "./v2-prompt-codec";

export type V2QuoteReviewErrorCode = "invalid_input" | "mismatch" | "unavailable" | "cancelled" | "timeout";
export type V2QuoteReviewFailureReason = "expired" | "not_yet_valid" | "insufficient_balance" | "unknown";

const ERROR_TEXT: Record<V2QuoteReviewErrorCode, string> = {
  invalid_input: "The V2 quote could not be reviewed.",
  mismatch: "The V2 quote does not match the current registry state.",
  unavailable: "The V2 quote could not be reviewed right now.",
  cancelled: "The V2 quote review was cancelled.",
  timeout: "The V2 quote review timed out.",
};

export class V2QuoteReviewError extends Error {
  constructor(readonly code: V2QuoteReviewErrorCode, readonly reason: V2QuoteReviewFailureReason = "unknown") {
    super(ERROR_TEXT[code]);
    this.name = "V2QuoteReviewError";
  }
}

export interface V2QuoteReadAdapter {
  request(args: { method: string; params?: readonly unknown[] }, signal: AbortSignal): Promise<unknown>;
}

export interface V2QuoteReviewInput {
  readonly deployment: DeploymentReference;
  readonly reservation: WalletReservationResult;
  readonly payer: Address;
  readonly tierId: number;
  readonly name: string;
  readonly additionalPrompt: string;
  readonly systemPrompt: SystemPrompt;
  readonly rpc: V2QuoteReadAdapter;
  readonly operation: WalletOperation;
}

export type V2QuoteReviewObservation = Readonly<{
  kind: "observation";
  deployment: DeploymentReference;
  reservationId: Hex;
  payer: Address;
  tierId: number;
  name: string;
  blockNumber: string;
  blockHash: Hex;
  blockTimestamp: string;
  usdcAddress: Address;
  spender: Address;
  price: string;
  balance: string;
  allowance: string;
  approvalRequired: boolean;
  basePromptText: string;
  additionalPrompt: string;
  combinedPrompt: string;
}>;

const MAX_SAFE_INTEGER = BigInt(Number.MAX_SAFE_INTEGER);
const MAX_UINT64 = (1n << 64n) - 1n;
const MAX_UINT96 = (1n << 96n) - 1n;
const MAX_UINT256 = (1n << 256n) - 1n;
const MAX_CALLS = 32;
const MAX_CODE_BYTES = 24_576;
const MAX_HEADER_KEYS = 32;
const MAX_RECEIPT_KEYS = 32;
const DEADLINE_MS = 8_000;
const EMPTY_KECCAK: Hex = "0xc5d2460186f7233c927e7db2dcc703c0e500b653ca82273b7bfad8045d85a470";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const ZERO32 = `0x${"00".repeat(32)}`;
const SYSTEM_PROMPT_TYPEHASH = keccak256(toBytes("BotnetRegistryV2SystemPrompt(uint8 version,uint8 encoding,uint32 chunks,bytes32 encodedDigest,bytes32 decodedDigest,uint32 decodedBytes)"));
const ABI_RETURN_WORDS: Readonly<Record<string, number>> = Object.freeze({
  USDC: 1, factory: 1, admissionsOpen: 1, systemPromptSealed: 1, systemPromptHash: 1,
  runtimePolicyHash: 1, accountPolicyHash: 1, settlementPolicyHash: 1, quoteSigner: 1,
  quoteSignerEpoch: 1, payWindow: 1, uploadWindow: 1, fulfillWindow: 1, tiers: 3,
  capacitySlots: 5, reservationConsumed: 1, quoteNonceConsumed: 1, hashVoucher: 1,
});

const DEPLOYMENT_KEYS = [
  "schemaVersion", "protocol", "chainId", "registryAddress", "deploymentBlockNumber", "deploymentBlockHash",
  "deploymentTransactionHash", "registryRuntimeCodeHash", "sourceCommitment",
] as const;
const RESERVATION_KEYS = ["kind", "schemaVersion", "reservationId", "state", "voucher", "signature", "deployment"] as const;
const VOUCHER_KEYS = [
  "creator", "payer", "reservationId", "capacitySlotId", "slotEpoch", "tierId", "price", "feeReceiver", "nameHash",
  "promptEncoding", "promptChunks", "promptRoot", "encodedPromptDigest", "decodedPromptDigest", "encodedPromptBytes",
  "decodedPromptBytes", "systemPromptHash", "runtimePolicyHash", "accountPolicyHash", "settlementPolicyHash",
  "quoteSignerEpoch", "quoteNonce", "issuedAt", "payBefore", "uploadBefore", "fulfillBefore",
] as const;
const SYSTEM_KEYS = [
  "schemaVersion", "protocol", "chainId", "registryAddress", "encoding", "chunkCount", "finalized", "text",
  "displayText", "encodedDigest", "decodedDigest", "decodedBytes", "systemPromptHash",
] as const;
const SYSTEM_OPTIONAL_KEYS = ["decodeError"] as const;

const voucherTypes = {
  AdmissionVoucher: [
    { name: "creator", type: "address" }, { name: "payer", type: "address" },
    { name: "reservationId", type: "bytes32" }, { name: "capacitySlotId", type: "bytes32" },
    { name: "slotEpoch", type: "uint64" }, { name: "tierId", type: "uint32" },
    { name: "price", type: "uint96" }, { name: "feeReceiver", type: "address" },
    { name: "nameHash", type: "bytes32" }, { name: "promptEncoding", type: "uint8" },
    { name: "promptChunks", type: "uint32" }, { name: "promptRoot", type: "bytes32" },
    { name: "encodedPromptDigest", type: "bytes32" }, { name: "decodedPromptDigest", type: "bytes32" },
    { name: "encodedPromptBytes", type: "uint32" }, { name: "decodedPromptBytes", type: "uint32" },
    { name: "systemPromptHash", type: "bytes32" }, { name: "runtimePolicyHash", type: "bytes32" },
    { name: "accountPolicyHash", type: "bytes32" }, { name: "settlementPolicyHash", type: "bytes32" },
    { name: "quoteSignerEpoch", type: "uint64" }, { name: "quoteNonce", type: "uint256" },
    { name: "issuedAt", type: "uint64" }, { name: "payBefore", type: "uint64" },
    { name: "uploadBefore", type: "uint64" }, { name: "fulfillBefore", type: "uint64" },
  ],
} as const;

const erc20ReadAbi = [
  { type: "function", name: "balanceOf", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ type: "uint256" }] },
  { type: "function", name: "allowance", stateMutability: "view", inputs: [{ name: "owner", type: "address" }, { name: "spender", type: "address" }], outputs: [{ type: "uint256" }] },
] as const;

type OwnRecord = Record<string, unknown>;
type Snapshot = {
  deployment: DeploymentReference;
  reservation: Extract<WalletReservationResult, { kind: "voucher" }>;
  voucher: V2AdmissionVoucher;
  payer: Address;
  tierId: number;
  name: string;
  additionalPrompt: string;
  systemPrompt: SystemPrompt;
  basePromptText: string;
  combinedPrompt: string;
  localVoucherHash: Hex;
  signerSignature: Hex;
};
type Controls = {
  readonly adapter: object;
  readonly request: V2QuoteReadAdapter["request"];
  readonly operation: object;
  readonly parentSignal: AbortSignal;
  readonly isCurrent: WalletOperation["isCurrent"];
};
type ParsedHeader = { number: bigint; numberText: string; hash: Hex; timestamp: bigint; timestampText: string };

function error(code: V2QuoteReviewErrorCode, reason: V2QuoteReviewFailureReason = "unknown"): never {
  throw new V2QuoteReviewError(code, reason);
}

function ownDataRecord(value: unknown, required: readonly string[], optional: readonly string[] = [], maxKeys = required.length + optional.length, allowUnknown = false): OwnRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) error("invalid_input");
  let keys: PropertyKey[];
  let prototype: object | null;
  try {
    keys = Reflect.ownKeys(value);
    prototype = Object.getPrototypeOf(value);
  } catch { return error("invalid_input"); }
  if ((prototype !== Object.prototype && prototype !== null) || keys.length > maxKeys || keys.some((key) => typeof key !== "string")) error("invalid_input");
  const strings = keys as string[];
  if (required.some((key) => !strings.includes(key)) || (!allowUnknown && strings.some((key) => !required.includes(key) && !optional.includes(key)))) error("invalid_input");
  const record: OwnRecord = Object.create(null) as OwnRecord;
  for (const key of strings) {
    let descriptor: PropertyDescriptor | undefined;
    try { descriptor = Object.getOwnPropertyDescriptor(value, key); } catch { return error("invalid_input"); }
    if (!descriptor || !("value" in descriptor) || descriptor.enumerable !== true) error("invalid_input");
    record[key] = descriptor.value;
  }
  return record;
}

function copyDeployment(value: unknown): DeploymentReference {
  const item = ownDataRecord(value, DEPLOYMENT_KEYS);
  if (item.schemaVersion !== 1 || item.protocol !== REGISTRY_V2_PROTOCOL || item.chainId !== REGISTRY_V2_CHAIN_ID
    || !isLowerAddress(item.registryAddress) || !isCanonicalDecimal(item.deploymentBlockNumber, MAX_SAFE_INTEGER)
    || !isHash(item.deploymentBlockHash) || !isHash(item.deploymentTransactionHash) || !isHash(item.registryRuntimeCodeHash)
    || typeof item.sourceCommitment !== "string" || item.sourceCommitment.length !== 64 || !/^[0-9a-f]{64}$/.test(item.sourceCommitment)) error("invalid_input");
  return Object.freeze({
    schemaVersion: 1, protocol: REGISTRY_V2_PROTOCOL, chainId: REGISTRY_V2_CHAIN_ID,
    registryAddress: item.registryAddress, deploymentBlockNumber: item.deploymentBlockNumber,
    deploymentBlockHash: item.deploymentBlockHash, deploymentTransactionHash: item.deploymentTransactionHash,
    registryRuntimeCodeHash: item.registryRuntimeCodeHash, sourceCommitment: item.sourceCommitment,
  });
}

function snapshotControls(input: unknown): { values: OwnRecord; controls: Controls } {
  const values = ownDataRecord(input, ["deployment", "reservation", "payer", "tierId", "name", "additionalPrompt", "systemPrompt", "rpc", "operation"]);
  const adapter = values.rpc;
  const operation = values.operation;
  if (typeof adapter !== "object" || adapter === null || typeof operation !== "object" || operation === null) error("invalid_input");
  let request: unknown, signal: unknown, isCurrent: unknown, finish: unknown;
  try {
    request = (adapter as V2QuoteReadAdapter).request;
    signal = (operation as WalletOperation).signal;
    isCurrent = (operation as WalletOperation).isCurrent;
    finish = (operation as WalletOperation).finish;
  } catch { return error("invalid_input"); }
  if (typeof request !== "function" || typeof isCurrent !== "function" || typeof finish !== "function"
    || typeof AbortSignal === "undefined" || !(signal instanceof AbortSignal)) error("invalid_input");
  return {
    values,
    controls: {
      adapter,
      request: (request as V2QuoteReadAdapter["request"]).bind(adapter),
      operation,
      parentSignal: signal as AbortSignal,
      isCurrent: (isCurrent as WalletOperation["isCurrent"]).bind(operation),
    },
  };
}

function snapshotBusiness(values: OwnRecord): Snapshot {
  const deployment = copyDeployment(values.deployment);
  const reservationValue = ownDataRecord(values.reservation, RESERVATION_KEYS);
  if (reservationValue.kind !== "voucher" || reservationValue.schemaVersion !== 2 || reservationValue.state !== "quoted") error("invalid_input");
  const reservationDeployment = copyDeployment(reservationValue.deployment);
  if (!sameDeployment(deployment, reservationDeployment)) error("invalid_input");
  const reservationId = reservationValue.reservationId;
  if (!isHash(reservationId)) error("invalid_input");

  const voucherRecord = ownDataRecord(reservationValue.voucher, VOUCHER_KEYS);
  const voucher = copyVoucher(voucherRecord);
  if (voucher.reservationId !== reservationId) error("invalid_input");
  const signature = reservationValue.signature;
  if (!isSignature(signature)) error("invalid_input");

  const payer = values.payer;
  const tierId = values.tierId;
  const name = values.name;
  const additionalPrompt = values.additionalPrompt;
  if (!isLowerAddress(payer) || !Number.isSafeInteger(tierId) || (tierId as number) < 0 || (tierId as number) > 0xffff_ffff
    || typeof name !== "string" || name.length < 1 || name.length > 48 || !/^[A-Za-z0-9 _.-]+$/.test(name)
    || typeof additionalPrompt !== "string" || additionalPrompt.length > 64_000 || !wellFormedText(additionalPrompt)) error("invalid_input");
  if (voucher.creator !== payer || voucher.payer !== payer || BigInt(voucher.tierId) !== BigInt(tierId as number)
    || voucher.nameHash !== keccak256(toHex(new TextEncoder().encode(name)))) error("invalid_input");

  const extra = encodeV2Utf8Prompt(additionalPrompt);
  if (voucher.promptEncoding !== extra.encoding || voucher.promptChunks !== extra.chunkCount || voucher.promptRoot !== extra.root
    || voucher.encodedPromptDigest !== extra.encodedDigest || voucher.decodedPromptDigest !== extra.decodedDigest
    || voucher.encodedPromptBytes !== extra.encodedBytes || voucher.decodedPromptBytes !== extra.decodedBytes) error("invalid_input");

  const system = copySystemPrompt(values.systemPrompt, deployment);
  const combinedPrompt = additionalPrompt.length === 0 ? system.text! : `${system.text!}\n\n${additionalPrompt}`;
  if (combinedPrompt.length > 64_000 || combinedPrompt.trim().length === 0) error("invalid_input");
  const combinedBytes = new TextEncoder().encode(JSON.stringify(combinedPrompt));
  if (combinedBytes.length > 64_000) error("invalid_input");

  const typedData = voucherTypedData(voucher, deployment.registryAddress);
  const localVoucherHash = hashTypedData(typedData);
  if (system.systemPromptHash !== voucher.systemPromptHash || system.systemPromptHash !== calculateSystemPromptHash(system)) error("invalid_input");

  return {
    deployment, reservation: Object.freeze({
      kind: "voucher", schemaVersion: 2, reservationId, state: "quoted", voucher, signature: signature as Hex, deployment,
    }),
    voucher, payer: payer as Address, tierId: tierId as number, name, additionalPrompt,
    systemPrompt: system, basePromptText: system.text!, combinedPrompt, localVoucherHash, signerSignature: signature as Hex,
  };
}

function copyVoucher(record: OwnRecord): V2AdmissionVoucher {
  if (!isLowerAddress(record.creator) || !isLowerAddress(record.payer) || !isHash(record.reservationId) || !isHash(record.capacitySlotId)
    || !isCanonicalDecimal(record.slotEpoch, MAX_UINT64) || !isSafeInteger(record.tierId, 0, 0xffff_ffff)
    || !isCanonicalDecimal(record.price, MAX_UINT96) || !isLowerAddress(record.feeReceiver) || record.feeReceiver === ZERO_ADDRESS
    || !isHash(record.nameHash) || record.promptEncoding !== 0 || !isSafeInteger(record.promptChunks, 0, 16)
    || !isHash(record.promptRoot, true) || !isHash(record.encodedPromptDigest, true) || !isHash(record.decodedPromptDigest, true)
    || !isSafeInteger(record.encodedPromptBytes, 0, 65_536) || !isSafeInteger(record.decodedPromptBytes, 0, 64_000)
    || !isHash(record.systemPromptHash) || !isHash(record.runtimePolicyHash) || !isHash(record.accountPolicyHash) || !isHash(record.settlementPolicyHash)
    || !isCanonicalDecimal(record.quoteSignerEpoch, MAX_UINT64) || !isCanonicalDecimal(record.quoteNonce, MAX_UINT256)
    || !isCanonicalDecimal(record.issuedAt, MAX_UINT64) || !isCanonicalDecimal(record.payBefore, MAX_UINT64)
    || !isCanonicalDecimal(record.uploadBefore, MAX_UINT64) || !isCanonicalDecimal(record.fulfillBefore, MAX_UINT64)) error("invalid_input");
  if (record.reservationId === ZERO32 || record.capacitySlotId === ZERO32
    || BigInt(record.slotEpoch as string) === 0n || BigInt(record.quoteSignerEpoch as string) === 0n) error("invalid_input");
  if ((record.promptChunks === 0) !== (record.promptRoot === ZERO32)
    || (record.promptChunks === 0) !== (record.encodedPromptBytes === 0)
    || (record.promptChunks === 0) !== (record.decodedPromptBytes === 0)) error("invalid_input");
  return Object.freeze({
    creator: record.creator as Address, payer: record.payer as Address, reservationId: record.reservationId as Hex,
    capacitySlotId: record.capacitySlotId as Hex, slotEpoch: record.slotEpoch as string, tierId: record.tierId as number,
    price: record.price as string, feeReceiver: record.feeReceiver as Address, nameHash: record.nameHash as Hex,
    promptEncoding: 0, promptChunks: record.promptChunks as number, promptRoot: record.promptRoot as Hex,
    encodedPromptDigest: record.encodedPromptDigest as Hex, decodedPromptDigest: record.decodedPromptDigest as Hex,
    encodedPromptBytes: record.encodedPromptBytes as number, decodedPromptBytes: record.decodedPromptBytes as number,
    systemPromptHash: record.systemPromptHash as Hex, runtimePolicyHash: record.runtimePolicyHash as Hex,
    accountPolicyHash: record.accountPolicyHash as Hex, settlementPolicyHash: record.settlementPolicyHash as Hex,
    quoteSignerEpoch: record.quoteSignerEpoch as string, quoteNonce: record.quoteNonce as string, issuedAt: record.issuedAt as string,
    payBefore: record.payBefore as string, uploadBefore: record.uploadBefore as string, fulfillBefore: record.fulfillBefore as string,
  });
}

function copySystemPrompt(value: unknown, deployment: DeploymentReference): SystemPrompt {
  const record = ownDataRecord(value, SYSTEM_KEYS, SYSTEM_OPTIONAL_KEYS);
  if (record.schemaVersion !== 2 || record.protocol !== REGISTRY_V2_PROTOCOL || record.chainId !== REGISTRY_V2_CHAIN_ID
    || record.registryAddress !== deployment.registryAddress || record.finalized !== true || !isSafeInteger(record.encoding, 0, 1)
    || !isSafeInteger(record.chunkCount, 0, 16) || typeof record.text !== "string" || record.text.length > 64_000
    || !wellFormedText(record.text) || !isHash(record.systemPromptHash) || Object.prototype.hasOwnProperty.call(record, "decodeError")) error("invalid_input");
  if (record.displayText !== null && (typeof record.displayText !== "string" || record.displayText.length > 64_000 || !wellFormedText(record.displayText))) error("invalid_input");
  if (!isHash(record.encodedDigest, true) || !isHash(record.decodedDigest, true) || !isSafeInteger(record.decodedBytes, 0, 64_000)) error("invalid_input");
  const bytes = new TextEncoder().encode(record.text as string);
  if (bytes.length !== record.decodedBytes) error("invalid_input");
  const decodedDigest = record.encoding === 0 && record.chunkCount === 0 && bytes.length === 0 ? EMPTY_KECCAK : sha256(toHex(bytes));
  if (record.decodedDigest !== decodedDigest) error("invalid_input");
  if (record.encoding === 0) {
    if (record.chunkCount === 0) {
      if (bytes.length !== 0 || record.encodedDigest !== EMPTY_KECCAK || record.encodedDigest !== record.decodedDigest) error("invalid_input");
    } else if (bytes.length === 0 || record.chunkCount > bytes.length || record.encodedDigest === ZERO32) error("invalid_input");
  } else if (record.chunkCount === 0 || record.encodedDigest === ZERO32) error("invalid_input");
  return Object.freeze({
    schemaVersion: 2, protocol: REGISTRY_V2_PROTOCOL, chainId: REGISTRY_V2_CHAIN_ID, registryAddress: deployment.registryAddress,
    encoding: record.encoding as number, chunkCount: record.chunkCount as number, finalized: true, text: record.text as string,
    displayText: record.displayText as string | null, encodedDigest: record.encodedDigest as Hex,
    decodedDigest: record.decodedDigest as Hex, decodedBytes: record.decodedBytes as number,
    systemPromptHash: record.systemPromptHash as Hex,
  });
}

function calculateSystemPromptHash(system: SystemPrompt): Hex {
  return keccak256(encodeAbiParameters(
    [{ type: "bytes32" }, { type: "uint8" }, { type: "uint8" }, { type: "uint32" }, { type: "bytes32" }, { type: "bytes32" }, { type: "uint32" }],
    [SYSTEM_PROMPT_TYPEHASH, 1, system.encoding!, system.chunkCount!, system.encodedDigest as Hex, system.decodedDigest as Hex, system.decodedBytes!],
  ));
}

function isLowerAddress(value: unknown): value is Address {
  return typeof value === "string" && value.length === 42 && /^0x[0-9a-f]{40}$/.test(value) && value !== ZERO_ADDRESS;
}
function isRpcAddress(value: unknown): value is Address {
  return typeof value === "string" && value.length === 42 && /^0x[0-9a-fA-F]{40}$/.test(value) && value.toLowerCase() !== ZERO_ADDRESS;
}
function isHash(value: unknown, allowZero = false): value is Hex {
  return typeof value === "string" && value.length === 66 && /^0x[0-9a-f]{64}$/.test(value) && (allowZero || value !== ZERO32);
}
function isSignature(value: unknown): value is Hex {
  if (typeof value !== "string" || value.length !== 132 || !/^0x[0-9a-f]{130}$/.test(value)) return false;
  const v = Number.parseInt(value.slice(-2), 16);
  const r = BigInt(`0x${value.slice(2, 66)}`);
  const s = BigInt(`0x${value.slice(66, 130)}`);
  const order = BigInt("0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141");
  const halfOrder = BigInt("0x7fffffffffffffffffffffffffffffff5d576e7357a4501ddfe92f46681b20a0");
  return (v === 27 || v === 28) && r > 0n && r < order && s > 0n && s <= halfOrder;
}
function isCanonicalDecimal(value: unknown, max: bigint): value is string {
  if (typeof value !== "string" || value.length === 0 || value.length > 78 || !/^(0|[1-9][0-9]*)$/.test(value)) return false;
  try { return BigInt(value) <= max; } catch { return false; }
}
function isSafeInteger(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= min && value <= max;
}
function wellFormedText(value: string): boolean {
  if (value.startsWith("\uFEFF")) return false;
  for (let i = 0; i < value.length; i++) {
    const c = value.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const next = value.charCodeAt(i + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return false;
      i++;
    } else if (c >= 0xdc00 && c <= 0xdfff) return false;
  }
  return true;
}
function sameDeployment(a: DeploymentReference, b: DeploymentReference): boolean {
  return DEPLOYMENT_KEYS.every((key) => a[key] === b[key]);
}
function voucherTypedData(voucher: V2AdmissionVoucher, verifyingContract: Address) {
  return {
    domain: { name: "BotnetRegistryV2", version: "1", chainId: REGISTRY_V2_CHAIN_ID, verifyingContract },
    types: voucherTypes,
    primaryType: "AdmissionVoucher" as const,
    message: {
      ...voucher, slotEpoch: BigInt(voucher.slotEpoch), quoteSignerEpoch: BigInt(voucher.quoteSignerEpoch),
      quoteNonce: BigInt(voucher.quoteNonce), issuedAt: BigInt(voucher.issuedAt), payBefore: BigInt(voucher.payBefore),
      uploadBefore: BigInt(voucher.uploadBefore), fulfillBefore: BigInt(voucher.fulfillBefore), price: BigInt(voucher.price),
    },
  };
}

function quantity(value: unknown): bigint | null {
  if (typeof value !== "string" || value.length < 3 || value.length > 66 || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/.test(value)) return null;
  try { return BigInt(value); } catch { return null; }
}
function rpcHash(value: unknown): value is Hex { return typeof value === "string" && value.length === 66 && /^0x[0-9a-f]{64}$/.test(value) && value !== ZERO32; }
function parseHeader(value: unknown): ParsedHeader | null {
  try {
    const row = ownDataRecord(value, ["number", "hash", "timestamp"], [], MAX_HEADER_KEYS, true);
    const number = quantity(row.number), timestamp = quantity(row.timestamp);
    if (number === null || timestamp === null || !rpcHash(row.hash)) return null;
    return { number, numberText: number.toString(), hash: row.hash, timestamp, timestampText: timestamp.toString() };
  } catch { return null; }
}

type RpcScope = {
  call(method: string, params: readonly unknown[]): Promise<unknown>;
  race<T>(pending: Promise<T>): Promise<T>;
  fail(code: V2QuoteReviewErrorCode, reason?: V2QuoteReviewFailureReason): void;
  check(): void;
  error(): V2QuoteReviewError | null;
  finish(): void;
};
function createRpcScope(controls: Controls, now: () => number, deadline: number): RpcScope {
  const child = new AbortController();
  let terminal: V2QuoteReviewError | null = null;
  let rejectTerminal!: (reason: unknown) => void;
  const terminalPromise = new Promise<never>((_, reject) => { rejectTerminal = reject; });
  void terminalPromise.catch(() => {});
  let count = 0;
  const stop = (code: V2QuoteReviewErrorCode, reason: V2QuoteReviewFailureReason = "unknown") => {
    if (terminal) return;
    let selectedCode = code;
    let selectedReason = reason;
    try {
      const time = now();
      if (!Number.isFinite(time)) { selectedCode = "unavailable"; selectedReason = "unknown"; }
      else if (time >= deadline && code !== "timeout") { selectedCode = "timeout"; selectedReason = "unknown"; }
    } catch { selectedCode = "unavailable"; selectedReason = "unknown"; }
    terminal = new V2QuoteReviewError(selectedCode, selectedReason);
    child.abort();
    rejectTerminal(terminal);
  };
  const check = () => {
    if (terminal) throw terminal;
    let time: number;
    try { time = now(); } catch { stop("unavailable"); throw terminal!; }
    if (!Number.isFinite(time)) { stop("unavailable"); throw terminal!; }
    if (time >= deadline) { stop("timeout"); throw terminal!; }
    if (controls.parentSignal.aborted) { stop("cancelled"); throw terminal!; }
    let current = false;
    try { current = controls.isCurrent(); } catch { current = false; }
    if (terminal) throw terminal;
    try { time = now(); } catch { stop("unavailable"); throw terminal!; }
    if (!Number.isFinite(time)) { stop("unavailable"); throw terminal!; }
    if (time >= deadline) { stop("timeout"); throw terminal!; }
    if (controls.parentSignal.aborted) { stop("cancelled"); throw terminal!; }
    if (!current) { stop("cancelled"); throw terminal!; }
  };
  const onParentAbort = () => stop("cancelled");
  let timer: ReturnType<typeof setTimeout> | undefined;
  const armRemaining = () => {
    const remaining = Math.max(0, deadline - now());
    timer = setTimeout(() => stop("timeout"), remaining);
  };
  try {
    controls.parentSignal.addEventListener("abort", onParentAbort, { once: true });
    armRemaining();
    if (controls.parentSignal.aborted) stop("cancelled");
  } catch {
    if (timer !== undefined) clearTimeout(timer);
    try { controls.parentSignal.removeEventListener("abort", onParentAbort); } catch { /* keep setup failures sanitized */ }
    child.abort();
    throw new V2QuoteReviewError("invalid_input");
  }
  return {
    async call(method, params) {
      check();
      count += 1;
      if (count > MAX_CALLS) { stop("mismatch"); throw terminal!; }
      let pending: Promise<unknown>;
      try { pending = Promise.resolve(controls.request.call(controls.adapter, { method, params }, child.signal)); }
      catch { stop("unavailable"); throw terminal!; }
      const observed = pending.then(
        (value) => ({ ok: true as const, value }),
        () => { stop("unavailable"); return { ok: false as const }; },
      );
      const result = await Promise.race([observed, terminalPromise]);
      check();
      if (!result.ok) { stop("unavailable"); throw terminal!; }
      return result.value;
    },
    race<T>(pending: Promise<T>) { return Promise.race([pending, terminalPromise]); },
    fail(code, reason) { stop(code, reason); },
    check,
    error() { return terminal; },
    finish() {
      if (timer !== undefined) clearTimeout(timer);
      try { controls.parentSignal.removeEventListener("abort", onParentAbort); } catch { /* do not expose adapter-controlled signal details */ }
    },
  };
}

function encodeCall(name: string, args: readonly unknown[] = []): Hex {
  return encodeFunctionData({ abi: botnetRegistryV2Abi as Abi, functionName: name as never, args: args as never });
}
function decodeCall(name: string, data: unknown): readonly unknown[] {
  const words = ABI_RETURN_WORDS[name];
  if (!words || typeof data !== "string" || data.length !== 2 + words * 64 || !/^0x(?:[0-9a-f]{2})+$/.test(data)) error("mismatch");
  try {
    const args = decodeFunctionResult({ abi: botnetRegistryV2Abi as Abi, functionName: name as never, data: data as Hex }) as unknown;
    const reencoded = encodeFunctionResult({ abi: botnetRegistryV2Abi as Abi, functionName: name as never, result: args as never });
    if (reencoded !== data) error("mismatch");
    return (Array.isArray(args) ? args : [args]) as readonly unknown[];
  } catch (cause) {
    if (cause instanceof V2QuoteReviewError) throw cause;
    error("mismatch");
  }
}
function encodeTokenCall(name: "balanceOf" | "allowance", args: readonly unknown[]): Hex {
  return encodeFunctionData({ abi: erc20ReadAbi as Abi, functionName: name, args: args as never });
}
function decodeTokenCall(name: "balanceOf" | "allowance", data: unknown): bigint {
  if (typeof data !== "string" || data.length !== 66 || !/^0x(?:[0-9a-f]{2})+$/.test(data)) error("mismatch");
  try {
    const value = decodeFunctionResult({ abi: erc20ReadAbi as Abi, functionName: name, data: data as Hex }) as bigint;
    if (encodeFunctionResult({ abi: erc20ReadAbi as Abi, functionName: name, result: value }) !== data || typeof value !== "bigint" || value < 0n || value > MAX_UINT256) error("mismatch");
    return value;
  } catch (cause) { if (cause instanceof V2QuoteReviewError) throw cause; error("mismatch"); }
}
function rpcData(value: unknown, maxBytes: number): Hex {
  if (typeof value !== "string" || value.length > 2 + maxBytes * 2 || (value.length & 1) !== 0 || !/^0x(?:[0-9a-f]{2})*$/.test(value)) error("mismatch");
  return value as Hex;
}
function blockTag(hash: Hex): Readonly<{ blockHash: Hex; requireCanonical: true }> {
  return Object.freeze({ blockHash: hash, requireCanonical: true as const });
}
async function boundedReads(tasks: readonly (() => Promise<readonly unknown[]>)[]): Promise<readonly (readonly unknown[])[]> {
  const values: (readonly unknown[])[] = [];
  for (let offset = 0; offset < tasks.length; offset += 4) {
    const group = tasks.slice(offset, offset + 4);
    values.push(...await Promise.all(group.map((task) => task())));
  }
  return values;
}

async function readContract(scope: RpcScope, to: Address, data: Hex, tag: unknown): Promise<unknown> {
  try { return await scope.call("eth_call", [{ to, data }, tag]); }
  catch (cause) { if (cause instanceof V2QuoteReviewError) throw cause; error("unavailable"); }
}

async function executeReview(snapshot: Snapshot, scope: RpcScope): Promise<V2QuoteReviewObservation> {
  function failMismatch(): never { return error("mismatch"); }
  try {
    const chain = quantity(await scope.call("eth_chainId", []));
    if (chain !== BigInt(REGISTRY_V2_CHAIN_ID)) failMismatch();

    const latest = parseHeader(await scope.call("eth_getBlockByNumber", ["latest", false]));
    if (!latest || latest.number > MAX_SAFE_INTEGER || latest.timestamp > MAX_UINT64
      || latest.number < BigInt(snapshot.deployment.deploymentBlockNumber)) failMismatch();
    const receiptRaw = await scope.call("eth_getTransactionReceipt", [snapshot.deployment.deploymentTransactionHash]);
    let receipt: OwnRecord;
    try { receipt = ownDataRecord(receiptRaw, ["transactionHash", "blockHash", "blockNumber", "status", "contractAddress"], [], MAX_RECEIPT_KEYS, true); }
    catch { failMismatch(); }
    const receiptNumber = quantity(receipt.blockNumber);
    if (receipt.transactionHash !== snapshot.deployment.deploymentTransactionHash || receipt.blockHash !== snapshot.deployment.deploymentBlockHash
      || receiptNumber === null || receiptNumber.toString() !== snapshot.deployment.deploymentBlockNumber || receipt.status !== "0x1"
      || receipt.contractAddress !== snapshot.deployment.registryAddress) failMismatch();
    const reference = parseHeader(await scope.call("eth_getBlockByNumber", [rpcBlock(snapshot.deployment.deploymentBlockNumber), false]));
    if (!reference || reference.hash !== snapshot.deployment.deploymentBlockHash || reference.number.toString() !== snapshot.deployment.deploymentBlockNumber) failMismatch();

    const tag = blockTag(latest.hash);
    const registryAddress = snapshot.deployment.registryAddress;
    const registryCode = rpcData(await scope.call("eth_getCode", [registryAddress, tag]), MAX_CODE_BYTES);
    if (registryCode === "0x" || keccak256(registryCode) !== snapshot.deployment.registryRuntimeCodeHash) failMismatch();
    const usdcRaw = await readContract(scope, registryAddress, encodeCall("USDC"), tag);
    const usdcValues = decodeCall("USDC", usdcRaw);
    const usdc = usdcValues[0];
    if (!isRpcAddress(usdc)) failMismatch();
    const usdcAddress = usdc.toLowerCase() as Address;
    const factoryValues = decodeCall("factory", await readContract(scope, registryAddress, encodeCall("factory"), tag));
    const factory = factoryValues[0];
    if (!isRpcAddress(factory)) failMismatch();
    const factoryAddress = factory.toLowerCase() as Address;
    for (const address of [usdcAddress, factoryAddress]) {
      const code = rpcData(await scope.call("eth_getCode", [address, tag]), MAX_CODE_BYTES);
      if (code === "0x") failMismatch();
    }

    const read = async (name: string, args: readonly unknown[] = []) => decodeCall(name, await readContract(scope, registryAddress, encodeCall(name, args), tag));
    const identityReads = await boundedReads([
      () => read("admissionsOpen"), () => read("systemPromptSealed"), () => read("systemPromptHash"),
      () => read("runtimePolicyHash"), () => read("accountPolicyHash"), () => read("settlementPolicyHash"),
    ]);
    const admissions = identityReads[0]!, sealed = identityReads[1]!, systemHash = identityReads[2]!, runtimeHash = identityReads[3]!, accountHash = identityReads[4]!, settlementHash = identityReads[5]!;
    if (admissions[0] !== true || sealed[0] !== true || systemHash[0] !== snapshot.voucher.systemPromptHash
      || runtimeHash[0] !== snapshot.voucher.runtimePolicyHash || accountHash[0] !== snapshot.voucher.accountPolicyHash
      || settlementHash[0] !== snapshot.voucher.settlementPolicyHash) failMismatch();

    const stateReads = await boundedReads([
      () => read("quoteSigner"), () => read("quoteSignerEpoch"), () => read("payWindow"), () => read("uploadWindow"),
      () => read("fulfillWindow"), () => read("tiers", [snapshot.tierId]), () => read("capacitySlots", [snapshot.voucher.capacitySlotId]),
      () => read("reservationConsumed", [snapshot.voucher.reservationId]),
      () => read("quoteNonceConsumed", [BigInt(snapshot.voucher.quoteSignerEpoch), snapshot.payer, BigInt(snapshot.voucher.quoteNonce)]),
      () => read("hashVoucher", [snapshot.voucher]),
    ]);
    const signerValue = stateReads[0]!, signerEpoch = stateReads[1]!, payWindow = stateReads[2]!, uploadWindow = stateReads[3]!, fulfillWindow = stateReads[4]!;
    const tier = stateReads[5]!, slot = stateReads[6]!, reservationUsed = stateReads[7]!, nonceUsed = stateReads[8]!, hashValue = stateReads[9]!;
    const signer = signerValue[0];
    if (!isRpcAddress(signer) || typeof signerEpoch[0] !== "bigint" || typeof payWindow[0] !== "bigint"
      || typeof uploadWindow[0] !== "bigint" || typeof fulfillWindow[0] !== "bigint"
      || signerEpoch[0] !== BigInt(snapshot.voucher.quoteSignerEpoch) || payWindow[0] === 0n || uploadWindow[0] === 0n || fulfillWindow[0] === 0n) failMismatch();
    const tierFeeReceiver = tier[1];
    if (!isRpcAddress(tierFeeReceiver) || tierFeeReceiver.toLowerCase() !== snapshot.voucher.feeReceiver
      || tier[0] !== BigInt(snapshot.voucher.price) || tier[2] !== true) failMismatch();
    if (slot[0] !== true || slot[1] !== true || slot[2] !== BigInt(snapshot.voucher.slotEpoch) || slot[3] !== false || slot[4] !== 0n) failMismatch();
    if (reservationUsed[0] !== false || nonceUsed[0] !== false || hashValue[0] !== snapshot.localVoucherHash) failMismatch();
    let recovered: Address;
    scope.check();
    try { recovered = await scope.race(recoverTypedDataAddress({ ...voucherTypedData(snapshot.voucher, snapshot.deployment.registryAddress), signature: snapshot.signerSignature })); }
    catch { failMismatch(); }
    scope.check();
    if (recovered.toLowerCase() !== signer.toLowerCase()) failMismatch();
    if (latest.timestamp < BigInt(snapshot.voucher.issuedAt)) error("mismatch", "not_yet_valid");
    if (latest.timestamp > BigInt(snapshot.voucher.payBefore)) error("mismatch", "expired");
    if (BigInt(snapshot.voucher.payBefore) !== BigInt(snapshot.voucher.issuedAt) + (payWindow[0] as bigint)
      || BigInt(snapshot.voucher.uploadBefore) !== BigInt(snapshot.voucher.issuedAt) + (uploadWindow[0] as bigint)
      || BigInt(snapshot.voucher.fulfillBefore) !== BigInt(snapshot.voucher.issuedAt) + (fulfillWindow[0] as bigint)) failMismatch();

    const [balanceRaw, allowanceRaw] = await Promise.all([
      readContract(scope, usdcAddress, encodeTokenCall("balanceOf", [snapshot.payer]), tag),
      readContract(scope, usdcAddress, encodeTokenCall("allowance", [snapshot.payer, registryAddress]), tag),
    ]);
    const balance = decodeTokenCall("balanceOf", balanceRaw);
    const allowance = decodeTokenCall("allowance", allowanceRaw);
    const price = BigInt(snapshot.voucher.price);
    if (balance < price) error("mismatch", "insufficient_balance");

    const finalHeader = parseHeader(await scope.call("eth_getBlockByNumber", [rpcBlock(latest.numberText), false]));
    if (!finalHeader || finalHeader.number !== latest.number || finalHeader.hash !== latest.hash || finalHeader.timestamp !== latest.timestamp) failMismatch();
    const observation = {
      kind: "observation" as const, deployment: snapshot.deployment, reservationId: snapshot.voucher.reservationId,
      payer: snapshot.payer, tierId: snapshot.tierId, name: snapshot.name, blockNumber: latest.numberText,
      blockHash: latest.hash, blockTimestamp: latest.timestampText, usdcAddress, spender: registryAddress,
      price: snapshot.voucher.price, balance: balance.toString(), allowance: allowance.toString(), approvalRequired: allowance < price,
      basePromptText: snapshot.basePromptText, additionalPrompt: snapshot.additionalPrompt, combinedPrompt: snapshot.combinedPrompt,
    };
    scope.check();
    return Object.freeze(observation);
  } catch (cause) {
    if (cause instanceof V2QuoteReviewError) {
      const code = cause.code === "invalid_input" ? "mismatch" : cause.code;
      scope.fail(code, cause.reason);
      throw scope.error() ?? cause;
    }
    scope.fail(cause instanceof TypeError || cause instanceof RangeError || cause instanceof SyntaxError ? "mismatch" : "unavailable");
    throw scope.error() ?? new V2QuoteReviewError("unavailable");
  } finally {
    scope.finish();
  }
}

function rpcBlock(decimal: string): Hex {
  return `0x${BigInt(decimal).toString(16)}`;
}

/** Read-only, bounded review of a quoted V2 reservation against its pinned registry state. */
export function reviewV2Quote(input: V2QuoteReviewInput): Promise<V2QuoteReviewObservation> {
  let now: () => number;
  let deadline: number;
  try {
    const clock = globalThis.performance;
    now = clock.now.bind(clock);
    const entered = now();
    if (!Number.isFinite(entered)) error("unavailable");
    deadline = entered + DEADLINE_MS;
  } catch (cause) {
    return Promise.reject(cause instanceof V2QuoteReviewError ? cause : new V2QuoteReviewError("unavailable"));
  }
  let controls: Controls;
  let scope: RpcScope | undefined;
  try {
    const captured = snapshotControls(input);
    controls = captured.controls;
    scope = createRpcScope(controls, now, deadline);
    const snapshot = snapshotBusiness(captured.values);
    scope.check();
    return executeReview(snapshot, scope);
  } catch (cause) {
    if (scope) {
      scope.fail(cause instanceof V2QuoteReviewError ? cause.code : "invalid_input");
      const failure = scope.error() ?? new V2QuoteReviewError("invalid_input");
      scope.finish();
      return Promise.reject(failure);
    }
    return Promise.reject(cause instanceof V2QuoteReviewError ? cause : new V2QuoteReviewError("invalid_input"));
  }
}

